import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { frentistaApi, apiError, formatBRL, formatL, PRODUTOS_LABEL } from "@/lib/api";
import Loader, { useMinLoading } from "@/components/Loader";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
         AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { modal } from "@/lib/modal";
import { Printer, Lock, LogOut, Receipt, Sparkles } from "lucide-react";

export default function Caixa() {
  const nav = useNavigate();
  const { frentista, logoutFrentista } = useAuth();
  const [atual, setAtual] = useState(null);
  const [ultimos, setUltimos] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closingReport, setClosingReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, uRes] = await Promise.all([
        frentistaApi.get("/caixa/atual").catch(() => ({ data: null })),
        frentistaApi.get("/caixa/ultimos"),
      ]);
      setAtual(aRes.data); setUltimos(uRes.data);
    } catch (err) { modal.error(apiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doFechar = async () => {
    setConfirmOpen(false);
    try {
      const { data } = await frentistaApi.post("/caixa/fechar", {});
      const { data: rep } = await frentistaApi.get(`/caixa/${data.caixa_id}/relatorio`);
      setClosingReport(rep);  // abre o "Relatório do caixa" (com selo ✓ Caixa fechado) — sem modal por cima
      setAtual(null);
    } catch (err) { modal.error(apiError(err)); }
  };

  // Ao fechar o relatório, SAI do sistema (evita o vendedor abrir outro caixa em seguida).
  const fecharEsair = () => {
    setClosingReport(null);
    logoutFrentista();
    nav("/");
  };

  const openDetail = async (caixaId) => {
    try {
      const { data } = await frentistaApi.get(`/caixa/${caixaId}/relatorio`);
      setDetail(data);
    } catch (err) { modal.error(apiError(err)); }
  };

  const showLoader = useMinLoading(loading);
  if (showLoader) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto px-3 md:px-8 pt-3 pb-6 space-y-4 float-in" data-testid="caixa-page">
      {atual ? (
        <section data-testid="caixa-atual" className="card-soft rounded-2xl overflow-hidden">
          <div className="p-3.5 md:p-5 flex items-center justify-between flex-wrap gap-2"
               style={{ background: "linear-gradient(135deg,#0F172A 0%,#1E293B 100%)" }}>
            <div className="text-white">
              <div className="chip mb-1 text-[10px] py-0.5" style={{ background: "rgba(16,185,129,0.18)", color: "#86EFAC" }}>
                <span className="pulse-dot" /> Caixa aberto
              </div>
              <h2 className="font-serif-display text-xl md:text-3xl text-white leading-tight">{atual.count} venda{atual.count !== 1 ? "s" : ""}</h2>
              <div className="text-[10px] text-white/55 mt-0.5">
                Desde {new Date(atual.caixa.aberto_em).toLocaleString("pt-BR")}
              </div>
            </div>
            <button data-testid="fechar-caixa-btn" onClick={() => setConfirmOpen(true)}
                    className="btn-accent h-9 px-3.5 text-[12px] font-medium flex items-center justify-center gap-1.5">
              <Lock size={13} /> Fechar
            </button>
          </div>
          <div className="grid grid-cols-3 divide-x divide-neutral-100">
            <Kpi label="Bruto" value={formatBRL(atual.total_bruto)} compact />
            <Kpi label="Líquido" value={formatBRL(atual.total_liquido)} compact />
            <Kpi label="Descontos" value={formatBRL(atual.total_desconto)} accent compact />
          </div>
          <OcorrenciasPremio dados={atual.ocorrencias_premio_sorteio} testid="caixa-atual-ocorrencias" className="px-3.5 md:px-5 pt-3" />
          <AtendimentosList items={atual.atendimentos} maxHeight="max-h-[55vh]" />
        </section>
      ) : (
        <div className="card-soft rounded-2xl p-4 flex items-start gap-3" data-testid="caixa-fechado-aviso"
             style={{ background: "linear-gradient(135deg,#FEF3C7 0%,#FDE68A 100%)" }}>
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
            <Receipt size={18} className="text-amber-700" />
          </div>
          <div>
            <div className="font-serif-display text-xl">Nenhum caixa aberto</div>
            <div className="text-[12px] text-neutral-700 mt-0.5">Atualize a página. Se persistir, saia e entre novamente.</div>
          </div>
        </div>
      )}

      <section data-testid="caixa-historico">
        <div className="chip mb-2 text-[10px] py-0.5"><Sparkles size={11} /> Histórico</div>
        <h3 className="font-serif-display text-xl md:text-3xl mb-3">Últimos 3 caixas fechados</h3>
        <div className="grid gap-3 md:grid-cols-3">
          {ultimos.map((c) => (
            <button key={c.caixa.id} onClick={() => openDetail(c.caixa.id)}
                    data-testid={`historico-caixa-${c.caixa.id}`}
                    className="card-soft rounded-2xl p-3.5 text-left">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                {new Date(c.caixa.aberto_em).toLocaleString("pt-BR")}
              </div>
              <div className="font-serif-display text-2xl mt-0.5">{formatBRL(c.total_liquido)}</div>
              <div className="text-[11px] text-neutral-500 mt-0.5">{c.count} vendas · Desc {formatBRL(c.total_desconto)}</div>
            </button>
          ))}
          {ultimos.length === 0 && <div className="text-[12px] text-neutral-500">Nenhum histórico ainda.</div>}
        </div>
      </section>

      {/* CONFIRM DIALOG */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-3xl border-none p-0 overflow-y-auto max-w-[92vw] sm:max-w-md">
          <div className="glass p-5 rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif-display text-2xl">Fechar caixa?</AlertDialogTitle>
              <AlertDialogDescription className="text-neutral-600 text-[13px]">
                Seu caixa será fechado e o <b>Relatório do caixa</b> será exibido (imprima se quiser). Ao tocar em <b>Fechar</b>, você sairá do sistema.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-4 gap-2">
              <AlertDialogCancel data-testid="confirm-fechar-cancel" className="rounded-full border-none btn-ghost h-11 px-5">Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-fechar-ok" onClick={doFechar} className="btn-flow h-11 px-5 rounded-full">
                <Lock size={13} className="mr-1.5" /> Fechar
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* CLOSING REPORT — mobile-first, fits a phone screen */}
      <Dialog open={!!closingReport} onOpenChange={(v) => !v && fecharEsair()}>
        <DialogContent
          data-testid="closing-report-dialog"
          className="p-0 border-none rounded-2xl bg-white sm:max-w-md
                     w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)]
                     max-h-[calc(100dvh-1rem)] flex flex-col overflow-hidden"
        >
          {closingReport && (
            <>
              {/* Print-only thermal copy */}
              <div id="print-area" className="thermal hidden print:block p-4 space-y-3 bg-white">
                <ThermalReceipt report={closingReport} frentista={frentista} />
              </div>

              <div className="px-4 pt-4 pb-2 no-print">
                <div className="font-serif-display text-2xl leading-tight">Relatório do caixa</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Tipo: {closingReport.caixa.fechamento_tipo || "manual"}</div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-3 space-y-3 no-print">
                <div className="grid grid-cols-3 rounded-xl overflow-hidden border border-neutral-200 divide-x divide-neutral-200">
                  <Kpi label="Bruto" value={formatBRL(closingReport.total_bruto)} compact />
                  <Kpi label="Líquido" value={formatBRL(closingReport.total_liquido)} compact />
                  <Kpi label="Descontos" value={formatBRL(closingReport.total_desconto)} accent compact />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Descontos por tipo</div>
                  <ul className="text-[12px] space-y-0.5">
                    {Object.entries(closingReport.descontos_por_tipo).map(([k, v]) => (
                      <li key={k}>• {k}: {formatBRL(v)}</li>
                    ))}
                    {Object.keys(closingReport.descontos_por_tipo).length === 0 && (
                      <li className="text-neutral-500">Sem descontos.</li>
                    )}
                  </ul>
                </div>
                <OcorrenciasPremio dados={closingReport.ocorrencias_premio_sorteio} testid="closing-ocorrencias" />
                <AtendimentosList items={closingReport.atendimentos} embedded />
              </div>

              {/* Ações: Imprimir + Fechar (Fechar = sai do sistema, evita abrir outro caixa em seguida) */}
              <div className="border-t border-neutral-200 p-3 flex gap-2 no-print bg-white shrink-0">
                <button data-testid="closing-print-btn" onClick={() => window.print()}
                        className="flex-1 h-11 btn-ghost text-[13px] font-medium flex items-center justify-center gap-1.5">
                  <Printer size={14} /> Imprimir
                </button>
                <button data-testid="closing-finish-btn" onClick={fecharEsair}
                        className="flex-1 h-11 btn-flow text-[13px] font-medium flex items-center justify-center gap-1.5">
                  <LogOut size={14} /> Fechar
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* DETAIL */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent
          className="p-0 border-none rounded-2xl bg-white sm:max-w-md
                     w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)]
                     max-h-[calc(100dvh-1rem)] flex flex-col overflow-hidden"
        >
          {detail && (
            <>
              <div className="px-4 pt-4 pb-2">
                <div className="font-serif-display text-2xl leading-tight">Relatório do caixa</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Tipo: {detail.caixa.fechamento_tipo || "manual"}</div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-3 space-y-3">
                <div className="grid grid-cols-3 rounded-xl overflow-hidden border border-neutral-200 divide-x divide-neutral-200">
                  <Kpi label="Bruto" value={formatBRL(detail.total_bruto)} compact />
                  <Kpi label="Líquido" value={formatBRL(detail.total_liquido)} compact />
                  <Kpi label="Descontos" value={formatBRL(detail.total_desconto)} accent compact />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Descontos por tipo</div>
                  <ul className="text-[12px] space-y-0.5">
                    {Object.entries(detail.descontos_por_tipo).map(([k, v]) => (
                      <li key={k}>• {k}: {formatBRL(v)}</li>
                    ))}
                    {Object.keys(detail.descontos_por_tipo).length === 0 && (
                      <li className="text-neutral-500">Sem descontos.</li>
                    )}
                  </ul>
                </div>
                <OcorrenciasPremio dados={detail.ocorrencias_premio_sorteio} testid="detalhe-ocorrencias" />
                <AtendimentosList items={detail.atendimentos} embedded />
              </div>

              <div className="border-t border-neutral-200 p-3 no-print bg-white shrink-0">
                <button onClick={() => window.print()} className="btn-ghost h-11 w-full text-[13px] font-medium flex items-center justify-center gap-2">
                  <Printer size={14} /> Imprimir
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent, compact }) {
  return (
    <div className={`${compact ? "p-2.5 md:p-3" : "p-4"} ${accent ? "bg-gradient-to-br from-amber-50 to-amber-100" : "bg-white"}`}>
      <div className="text-[9px] md:text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`font-serif-display ${compact ? "text-base md:text-2xl" : "text-3xl"} mt-0.5 leading-tight`}>{value}</div>
    </div>
  );
}

/**
 * Ocorrências do prêmio do sorteio no caixa (regra 1, aprovada pelo cliente em 24/10/2026): o prêmio
 * deixou de valer no meio do atendimento (usado em outra venda, cancelado, vencido) e a venda
 * finalizou com o desconto calculado. O bloco explica o fechamento; não há diferença a cobrar.
 */
function OcorrenciasPremio({ dados, testid, className = "" }) {
  const itens = Array.isArray(dados?.itens) ? dados.itens : [];
  if (!(Number(dados?.quantidade) > 0) || !itens.length) return null;
  return (
    <div data-testid={testid} className={className}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
        Ocorrências do prêmio do sorteio ({dados.quantidade}) · desconto mantido {formatBRL(dados.total_desconto_premio_reais)}
      </div>
      <ul className="text-[12px] space-y-1">
        {itens.map((o) => (
          <li key={o.atendimento_id}>
            • {o.cliente_nome || "Cliente"}: {formatBRL(o.valor_liquido)}
            <div className="text-[11px] text-neutral-500">{o.ocorrencia}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Compact mobile-first list. Replaces wide tables. */
function AtendimentosList({ items, embedded, maxHeight }) {
  if (!items || items.length === 0) {
    return <div className={`text-center text-[12px] text-neutral-500 py-5 ${embedded ? "" : "px-4"}`}>Nenhuma venda.</div>;
  }
  return (
    <ul className={`divide-y divide-neutral-100 ${maxHeight || ""} ${maxHeight ? "overflow-y-auto" : ""} ${embedded ? "" : ""}`}>
      {items.map((a) => (
        <li key={a.id} className="px-1 py-2 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-medium truncate">
              {a.cliente_nome || "—"}
            </div>
            <div className="text-[10px] text-neutral-500 truncate">
              {new Date(a.criado_em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {PRODUTOS_LABEL[a.produto] || a.produto}{a.produto === "troca_oleo" ? "" : ` · ${formatL(a.litros)}`}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[13px] font-medium tabular-nums">{formatBRL(a.valor_liquido)}</div>
            {a.desconto_total_reais > 0 && (
              <div className="text-[10px] text-rose-600 tabular-nums">−{formatBRL(a.desconto_total_reais)}</div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Thermal printer copy (kept simple, only renders during print). */
function ThermalReceipt({ report, frentista }) {
  return (
    <div className="space-y-2 text-[11px]">
      <div className="text-center pb-2 border-b border-dashed border-neutral-300">
        <div className="text-base font-bold">FECHAMENTO DE CAIXA</div>
        <div>{frentista?.unit_nome}</div>
        <div>Vendedor: {frentista?.nome} ({frentista?.username})</div>
        <div>Abertura: {new Date(report.caixa.aberto_em).toLocaleString("pt-BR")}</div>
        <div>Fechamento: {report.caixa.fechado_em ? new Date(report.caixa.fechado_em).toLocaleString("pt-BR") : "—"}</div>
      </div>
      <div>BRUTO ........ {formatBRL(report.total_bruto)}</div>
      <div>LIQUIDO ...... {formatBRL(report.total_liquido)}</div>
      <div>DESCONTOS .... {formatBRL(report.total_desconto)}</div>
      <div>VENDAS ....... {report.atendimentos.length}</div>
      {report.ocorrencias_premio_sorteio?.quantidade > 0 && (
        <div className="border-t border-dashed border-neutral-300 pt-2 mt-1">
          <div className="font-bold mb-1">OCORRENCIAS PREMIO DO SORTEIO ({report.ocorrencias_premio_sorteio.quantidade})</div>
          <div>DESCONTO MANTIDO {formatBRL(report.ocorrencias_premio_sorteio.total_desconto_premio_reais)}</div>
          {(report.ocorrencias_premio_sorteio.itens || []).map((o) => (
            <div key={o.atendimento_id} className="flex justify-between gap-2">
              <span className="truncate">{o.cliente_nome}</span>
              <span className="tabular-nums">{formatBRL(o.valor_liquido)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-dashed border-neutral-300 pt-2 mt-1">
        <div className="font-bold mb-1">VENDAS</div>
        {report.atendimentos.map((a) => (
          <div key={a.id} className="flex justify-between gap-2">
            <span className="truncate">{new Date(a.criado_em).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} {a.cliente_nome}</span>
            <span className="tabular-nums">{formatBRL(a.valor_liquido)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
